import { createFileRoute } from '@tanstack/react-router'
import React, { useState } from 'react'
import Layout from '@/components/Layout'
import { apiFetch } from '@/utils/api'
import { useI18n } from '@/hooks/useI18n'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { ArrowLeft } from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'
import { CurrentUserProvider } from '@/hooks/useCurrentUser'

interface CompanyFormData {
  name: string
  tax_id: string
  registration_no: string
  address: string
  is_active: boolean
}

function NewCompanyPage() {
  const navigate = useNavigate()
  const { t } = useI18n()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState<CompanyFormData>({
    name: '',
    tax_id: '',
    registration_no: '',
    address: '',
    is_active: true,
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const handleInputChange = (field: keyof CompanyFormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }))
  }

  const validate = () => {
    const e: Record<string, string> = {}
    if (!formData.name.trim()) e.name = t('forms.required', 'common')
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    setLoading(true)
    try {
      const payload = {
        name: formData.name.trim(),
        tax_id: formData.tax_id.trim() || undefined,
        registration_no: formData.registration_no.trim() || undefined,
        address: formData.address.trim() || undefined,
        is_active: formData.is_active,
      }
      const resp = await apiFetch('/api/companies/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (resp.ok) {
        navigate({ to: '/admin/finance' })
      } else {
        const data = await resp.json().catch(() => ({}))
        console.error('Create company failed', data)
        alert(t('finance.companies.createFailed', 'admin'))
      }
    } catch (err) {
      console.error('Error creating company', err)
      alert(t('finance.companies.createFailed', 'admin'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <CurrentUserProvider>
      <Layout>
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate({ to: '/admin/finance' })} className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" /> {t('finance.backToFinance', 'admin')}
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('finance.companies.newTitle', 'admin')}</h1>
              <p className="text-gray-600">{t('finance.companies.newSubtitle', 'admin')}</p>
            </div>
          </div>

          <Card className="max-w-2xl">
            <CardHeader>
              <CardTitle>{t('finance.companies.newDetailsTitle', 'admin')}</CardTitle>
              <CardDescription>{t('finance.companies.newDetailsDescription', 'admin')}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">{t('finance.companies.fields.name', 'admin')}</Label>
                    <Input id="name" value={formData.name} onChange={(e) => handleInputChange('name', e.target.value)} placeholder={t('finance.companies.placeholders.name', 'admin')} className={errors.name ? 'border-red-500' : ''} />
                    {errors.name && <p className="text-sm text-red-600">{errors.name}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="tax_id">{t('finance.companies.fields.taxId', 'admin')}</Label>
                    <Input id="tax_id" value={formData.tax_id} onChange={(e) => handleInputChange('tax_id', e.target.value)} placeholder={t('finance.companies.placeholders.taxIdNew', 'admin')} />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="registration_no">{t('finance.companies.fields.registrationNo', 'admin')}</Label>
                    <Input id="registration_no" value={formData.registration_no} onChange={(e) => handleInputChange('registration_no', e.target.value)} placeholder={t('finance.companies.placeholders.registrationNo', 'admin')} />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="address">{t('finance.companies.fields.address', 'admin')}</Label>
                    <Input id="address" value={formData.address} onChange={(e) => handleInputChange('address', e.target.value)} placeholder={t('finance.companies.placeholders.addressNew', 'admin')} />
                  </div>

                  <div className="flex items-center space-x-2 md:col-span-2">
                    <Checkbox id="is_active" checked={formData.is_active} onCheckedChange={(c) => handleInputChange('is_active', c as boolean)} />
                    <Label htmlFor="is_active">{t('status.active', 'common')}</Label>
                  </div>
                </div>

                <div className="flex justify-end space-x-4">
                  <Button type="button" variant="outline" onClick={() => navigate({ to: '/admin/finance' })}>
                    {t('actions.cancel', 'common')}
                  </Button>
                  <Button type="submit" disabled={loading}>
                    {loading ? t('finance.companies.creating', 'admin') : t('finance.companies.createAction', 'admin')}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </Layout>
    </CurrentUserProvider>
  )
}

export const Route = createFileRoute('/admin/finance/companies/new')({
  component: NewCompanyPage,
})
